import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfig } from '../../../contexts/ConfigContext';
import { formatPhoneE164, getSmsDeepLink, getWhatsAppDeepLink } from '../../../lib/messaging';

interface CustomerResult {
  id: string;
  customer_name: string;
  email: string;
  phone: string | null;
  mobile: string | null;
}

interface GeneratedReferralCode {
  code: string;
  times_used?: number;
}

/**
 * Mobile Referral Share Modal
 * Streamlined flow: Search customer -> Auto-create code -> Share via WhatsApp
 * WhatsApp templates are fetched from the database
 */
function MobileReferralShareModal({ onClose, customer }: { onClose: () => void; customer?: CustomerResult }) {
  const { supabase, hasPermission } = useAuth();
  // Only users with create rights may generate a code. Everyone else (e.g.
  // operators, who can view + share) looks up the existing code and never
  // creates one. Customer referral codes are auto-created, so a code almost
  // always exists already.
  const canCreateCode = hasPermission('referrals', 'contribute');
  const { formatCurrency, countryCode, countryConfig } = useConfig();
  const phonePrefix = (countryConfig as unknown as Record<string, string>)?.phone_prefix;

  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CustomerResult[]>([]);
  const [recentCustomers, setRecentCustomers] = useState<CustomerResult[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  // Selected customer state
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerResult | null>(null);
  const [referralCode, setReferralCode] = useState<GeneratedReferralCode | null>(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // WhatsApp templates from database
  const [templates, setTemplates] = useState<Record<string, string>>({});

  // Load recent customers and templates on mount
  useEffect(() => {
    loadRecentCustomers();
    loadTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When opened with a pre-selected customer (e.g. from the booking modal),
  // skip the search and jump straight to that customer's code + share screen.
  useEffect(() => {
    if (customer) { void selectCustomer(customer); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  // Load WhatsApp templates from database
  async function loadTemplates() {
    try {
      const { data, error: fetchError } = await supabase
        .from('whatsapp_templates')
        .select('template_key, message_template')
        .eq('country_code', countryCode || 'DK')
        .eq('is_active', true)
        .in('template_key', ['referral_customer_explain', 'referral_share_template']);

      if (!fetchError && data) {
        const templateMap: Record<string, string> = {};
        data.forEach((t: { template_key: string; message_template: string }) => { templateMap[t.template_key] = t.message_template; });
        setTemplates(templateMap);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  }

  // Substitute variables in template
  function applyTemplate(templateKey: string, variables: Record<string, string>) {
    let template = templates[templateKey];
    if (!template) return null;

    Object.entries(variables).forEach(([key, value]) => {
      template = template!.replace(new RegExp(`\\{${key}\\}`, 'g'), value || '');
    });
    return template;
  }

  async function loadRecentCustomers() {
    setLoadingRecent(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('customers')
        .select('id, customer_name, email, phone, mobile')
        .order('updated_at', { ascending: false })
        .limit(5);

      if (!fetchError && data) {
        setRecentCustomers(data);
      }
    } catch (err) {
      console.error('Error loading recent customers:', err);
    } finally {
      setLoadingRecent(false);
    }
  }

  async function handleSearch() {
    if (!searchTerm.trim()) return;

    setSearching(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('customers')
        .select('id, customer_name, email, phone, mobile')
        .or(`email.ilike.%${searchTerm}%,customer_name.ilike.%${searchTerm}%`)
        .limit(10);

      if (fetchError) throw fetchError;

      setSearchResults(data || []);
      if (data?.length === 0) {
        setError('No customers found');
      }
    } catch (err: unknown) {
      console.error('Error searching:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSearching(false);
    }
  }

  async function selectCustomer(customer: CustomerResult) {
    setSelectedCustomer(customer);
    setGeneratingCode(true);
    setError(null);

    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;

      if (canCreateCode) {
        // Manager/admin: generate the code if the customer doesn't have one yet.
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/referral-code-generate?operation=generate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ customer_id: customer.id })
          }
        );

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to generate code');
        }
        setReferralCode(data.code);
      } else {
        // Send-only: look up the existing code, never create one.
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/referral-code-generate?operation=check&customer_id=${customer.id}`,
          {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to look up referral code');
        }
        if (data.has_code && data.code) {
          setReferralCode(data.code);
        } else {
          setSelectedCustomer(null);
          setError('This customer does not have a referral code yet. Ask a manager to create one.');
        }
      }
    } catch (err: unknown) {
      console.error('Error fetching code:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setGeneratingCode(false);
    }
  }

  async function copyCode() {
    if (referralCode?.code) {
      await navigator.clipboard.writeText(referralCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  // Format phone number for wa.me / sms: links. Reads the country dialling
  // prefix from country_config so DK uses 45 and ZA uses 27 automatically.
  function getWhatsAppNumber(): string {
    const rawNumber = selectedCustomer?.mobile || selectedCustomer?.phone || '';
    return formatPhoneE164(rawNumber, phonePrefix) || '';
  }

  // Share explanation TO the customer
  function shareExplanationToCustomer() {
    if (!referralCode?.code) return;

    const firstName = selectedCustomer?.customer_name?.split(' ')[0] || '';

    let message = applyTemplate('referral_customer_explain', {
      customer_first_name: firstName,
      code: referralCode.code
    });

    if (!message) {
      const discount = formatCurrency ? formatCurrency(250) : '250 DKK';
      const credit = formatCurrency ? formatCurrency(250) : '250 DKK';
      message = `Hi ${firstName}! Just wanted to let you know about your CleanMyBed referral code: ${referralCode.code}\n\nWhen you share this with friends or family, they'll get ${discount} off their first mattress sanitisation. And you'll earn ${credit} credit towards your next service when they book.\n\nI've put together a message you can forward - just tap share and send it to anyone you think might benefit from healthier sleep!`;
    }

    const waNumber = getWhatsAppNumber();
    if (!waNumber) return;
    window.open(getWhatsAppDeepLink(waNumber, message), '_blank');
  }

  // SMS variant, same message to the same customer's phone
  function shareExplanationToCustomerSms() {
    if (!referralCode?.code) return;
    const firstName = selectedCustomer?.customer_name?.split(' ')[0] || '';
    let message = applyTemplate('referral_customer_explain', {
      customer_first_name: firstName,
      code: referralCode.code
    });
    if (!message) {
      const discount = formatCurrency ? formatCurrency(250) : '250 DKK';
      const credit = formatCurrency ? formatCurrency(250) : '250 DKK';
      message = `Hi ${firstName}! Just wanted to let you know about your CleanMyBed referral code: ${referralCode.code}\n\nWhen you share this with friends or family, they'll get ${discount} off their first mattress sanitisation. And you'll earn ${credit} credit towards your next service when they book.\n\nI've put together a message you can forward - just tap share and send it to anyone you think might benefit from healthier sleep!`;
    }
    const waNumber = getWhatsAppNumber();
    if (!waNumber) return;
    window.location.href = getSmsDeepLink(waNumber, message);
  }

  // Share template FOR the customer to forward to friends, sent to the customer
  function shareViaWhatsApp() {
    if (!referralCode?.code) return;

    let message = applyTemplate('referral_share_template', {
      code: referralCode.code
    });

    if (!message) {
      const discount = formatCurrency ? formatCurrency(250) : '250 DKK';
      message = `Hi! I've been using CleanMyBed for professional mattress sanitisation and wanted to share it with you. They deep clean mattresses to remove dust mites, allergens and bacteria - it really does make a difference for sleep quality.\n\nI have a code that gives you ${discount} off your first service: ${referralCode.code}\n\nHave a look at cleanmybed.com if you're interested. No pressure at all, just thought of you!`;
    }

    const waNumber = getWhatsAppNumber();
    if (!waNumber) return;
    window.open(getWhatsAppDeepLink(waNumber, message), '_blank');
  }

  // SMS variant, sent to the customer's phone
  function shareViaSms() {
    if (!referralCode?.code) return;
    let message = applyTemplate('referral_share_template', { code: referralCode.code });
    if (!message) {
      const discount = formatCurrency ? formatCurrency(250) : '250 DKK';
      message = `Hi! I've been using CleanMyBed for professional mattress sanitisation and wanted to share it with you. They deep clean mattresses to remove dust mites, allergens and bacteria - it really does make a difference for sleep quality.\n\nI have a code that gives you ${discount} off your first service: ${referralCode.code}\n\nHave a look at cleanmybed.com if you're interested. No pressure at all, just thought of you!`;
    }
    const waNumber = getWhatsAppNumber();
    if (!waNumber) return;
    window.location.href = getSmsDeepLink(waNumber, message);
  }

  function goBack() {
    setSelectedCustomer(null);
    setReferralCode(null);
    setError(null);
  }

  const whatsAppIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );

  // Show code screen when customer selected and code generated
  if (selectedCustomer && referralCode) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <button
              onClick={goBack}
              style={{ background: 'none', border: 'none', fontSize: 'var(--text-4xl)', cursor: 'pointer', padding: '0.5rem' }}
            >
              &larr;
            </button>
            <h2 style={{ flex: 1, textAlign: 'center', marginRight: '2rem' }}>Share Referral</h2>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>

          <div className="modal-body" style={{ padding: '1.5rem 1rem' }}>
            {/* Customer info */}
            <div className="text-center mb-xl">
              <div className="text-base fw-600">{selectedCustomer.customer_name}</div>
              <div className="text-sm text-light">{selectedCustomer.email}</div>
            </div>

            {/* Referral code display */}
            <div style={{
              textAlign: 'center',
              padding: '1.5rem',
              background: 'var(--color-bg)',
              borderRadius: 'var(--radius-lg, 12px)',
              marginBottom: '1.5rem'
            }}>
              <div className="text-sm text-light mb-sm">
                Referral Code
              </div>
              <div style={{
                fontSize: 'var(--text-5xl)',
                fontFamily: 'monospace',
                fontWeight: 700,
                color: 'var(--color-primary)',
                letterSpacing: '0.05em'
              }}>
                {referralCode.code}
              </div>
            </div>

            {/* Step 1: Send explanation to the customer */}
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              borderRadius: 'var(--radius, 8px)',
              border: '1px solid #93c5fd',
              marginBottom: '1rem'
            }}>
              <div style={{ fontWeight: 600, fontSize: 'var(--text-base)', marginBottom: '0.5rem', color: 'var(--color-info-text)' }}>
                Step 1: Explain to {selectedCustomer?.customer_name?.split(' ')[0] || 'Customer'}
              </div>
              <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-info-text)', marginBottom: '0.75rem', lineHeight: 1.4 }}>
                Send them a message explaining their referral code and how the rewards work.
              </div>
              <button
                className="btn btn-whatsapp w-full mb-sm"
                onClick={shareExplanationToCustomer}
              >
                {whatsAppIcon}
                Send via WhatsApp
              </button>
              <button
                className="btn btn-primary w-full"
                onClick={shareExplanationToCustomerSms}
              >
                📲 Send via SMS
              </button>
            </div>

            {/* Step 2: Give them a template to forward */}
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
              borderRadius: 'var(--radius, 8px)',
              border: '1px solid #bbf7d0',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontWeight: 600, fontSize: 'var(--text-base)', marginBottom: '0.5rem', color: 'var(--color-success-text)' }}>
                Step 2: Give Them a Share Template
              </div>
              <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-success-text)', marginBottom: '0.75rem', lineHeight: 1.4 }}>
                A ready-to-forward message they can send to friends and family.
              </div>
              <button
                className="btn btn-whatsapp w-full mb-sm"
                onClick={shareViaWhatsApp}
              >
                {whatsAppIcon}
                Share via WhatsApp
              </button>
              <button
                className="btn btn-primary w-full"
                onClick={shareViaSms}
              >
                📲 Share via SMS
              </button>
            </div>

            {/* Copy code button */}
            <button
              className="btn btn-outline"
              onClick={copyCode}
              style={{ width: '100%', padding: '0.875rem' }}
            >
              {copied ? '✓ Copied' : 'Copy Code Only'}
            </button>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={onClose}
              style={{ width: '100%', padding: '1rem' }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state when generating code
  if (selectedCustomer && generatingCode) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
          <div className="modal-header">
            <h2>Share Referral</h2>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div className="spinner" style={{ margin: '0 auto 1rem' }} />
            <div className="text-light">
              Getting referral code for {selectedCustomer.customer_name}...
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Search screen
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>Share Referral</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-danger mb-lg">
              {error}
            </div>
          )}

          {/* Search input */}
          <div className="form-group">
            <label className="form-label">Find Customer</label>
            <div className="flex gap-sm">
              <input
                type="text"
                className="form-input"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1, fontSize: 'var(--text-xl)', padding: '0.875rem' }}
                autoFocus
              />
              <button
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={searching || !searchTerm.trim()}
                style={{ padding: '0 1.25rem' }}
              >
                {searching ? '...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Search results */}
          {searchResults.length > 0 && (
            <div className="mt-lg">
              <div className="text-sm fw-600 mb-sm text-light">
                Search Results
              </div>
              {searchResults.map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => selectCustomer(customer)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.875rem',
                    background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius, 8px)',
                    marginBottom: '0.5rem',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div>
                    <div className="fw-600">{customer.customer_name}</div>
                    <div className="text-sm text-light">{customer.email}</div>
                  </div>
                  <span className="text-primary text-xl">&rarr;</span>
                </button>
              ))}
            </div>
          )}

          {/* Recent customers (shown when no search results) */}
          {searchResults.length === 0 && recentCustomers.length > 0 && (
            <div className="mt-xl">
              <div className="text-sm fw-600 mb-sm text-light">
                Recent Customers
              </div>
              {loadingRecent ? (
                <div className="text-center text-light p-lg">
                  Loading...
                </div>
              ) : (
                recentCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => selectCustomer(customer)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.875rem',
                      background: 'white',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius, 8px)',
                      marginBottom: '0.5rem',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div>
                      <div className="fw-500">{customer.customer_name}</div>
                      <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>{customer.email}</div>
                    </div>
                    <span className="text-light text-xl">&rarr;</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            style={{ width: '100%', padding: '1rem' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileReferralShareModal;
